import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2 } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCatalogLogs, useUploadCatalogImage } from './catalog.api';

/** Bits both the departments and the categories page need. */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

/**
 * The tile as the storefront draws it: the image on the department's own
 * background, inside its own border colour. Shown in the list and live in the
 * editor, because two hex fields tell nobody what the result looks like.
 */
export function MenuTile({
  imageUrl,
  color,
  borderColor,
  size = 'md',
}: {
  imageUrl?: string | null;
  color?: string | null;
  borderColor?: string | null;
  size?: 'sm' | 'md';
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg border-4',
        size === 'sm' ? 'size-12' : 'size-20'
      )}
      style={{
        background: color ?? undefined,
        borderColor: borderColor ?? undefined,
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className={cn('object-contain', size === 'sm' ? 'size-8' : 'size-14')}
        />
      ) : null}
    </div>
  );
}

/**
 * A hex field with a swatch that opens the browser's own colour picker.
 *
 * The original mounted a full palette widget under each field, which is 200px
 * of chrome for a value people paste. The native picker is one tap and knows
 * the platform's own recent colours.
 */
export function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
}) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(value ?? '') ? value! : '#ffffff';

  return (
    <Field label={label}>
      <div className="flex gap-2">
        <Input
          value={value ?? ''}
          placeholder="#ffffff"
          onChange={(event) => onChange(event.target.value)}
          className="font-mono"
        />
        <input
          type="color"
          aria-label={label}
          value={safe}
          onChange={(event) => onChange(event.target.value)}
          className="border-input size-9 shrink-0 cursor-pointer rounded-md border bg-transparent p-1"
        />
      </div>
    </Field>
  );
}

/** Upload or paste — the original only pasted, and a URL is not always to hand. */
export function ImageField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string | null;
  onChange: (url: string) => void;
}) {
  const { t } = useTranslation();
  const upload = useUploadCatalogImage();
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <Field label={label} hint={hint}>
      <div className="space-y-2">
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            upload.mutate(file, {
              onSuccess: onChange,
              onError: () => toast.error(t('catalog.uploadFailed')),
            });
            event.target.value = '';
          }}
        />
        <Button
          variant="outline"
          className="w-full border-dashed"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {upload.isPending ? <Loader2 className="animate-spin" /> : <ImagePlus />}
          {t('catalog.uploadImage')}
        </Button>
        <Input
          value={value ?? ''}
          placeholder={t('catalog.imageUrl')}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </Field>
  );
}

const LOGS_PAGE_SIZE = 20;

/** The change history of one department or category. */
export function LogsSheet({
  departmentId,
  categoryId,
  title,
  open,
  onOpenChange,
}: {
  departmentId?: string | null;
  categoryId?: string | null;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const logs = useCatalogLogs({
    departmentId: open ? departmentId : null,
    categoryId: open ? categoryId : null,
    page,
    size: LOGS_PAGE_SIZE,
  });

  const rows = logs.data?.logs ?? [];
  const total = logs.data?.count ?? 0;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setPage(0);
      }}
    >
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          {/* `isLoading`, not `isPending`: the query is disabled until there
              is an id, and a disabled query stays pending forever. */}
          {logs.isLoading && (
            <div className="divide-y rounded-xl border">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="space-y-1.5 p-3">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          )}

          {logs.isError && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('catalog.logsFailed')}
            </p>
          )}

          {logs.isSuccess && rows.length === 0 && (
            <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
              {t('catalog.noLogs')}
            </p>
          )}

          {rows.length > 0 && (
            <>
              <div className="relative rounded-xl">
                <BusyOverlay show={logs.isFetching} />
                <div
                  className={cn(
                    'divide-y rounded-xl border',
                    logs.isFetching && 'opacity-60 transition-opacity'
                  )}
                >
                  {rows.map((log, index) => (
                    <div key={log.id ?? index} className="space-y-0.5 p-3">
                      <p className="text-muted-foreground text-xs">
                        {formatDateTime(log.createdAt)}
                      </p>
                      <p className="text-sm break-words">{log.message}</p>
                    </div>
                  ))}
                </div>
              </div>
              <Pagination
                page={page}
                size={LOGS_PAGE_SIZE}
                total={total}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
